import { useEffect, useMemo, useRef } from 'react'
import { cn } from './internal/utils'
import {
  FieldPrefixContext,
  FormContext,
  useForm,
  useFormInstance,
  useWatch,
} from './form/context'
import FormItem from './form/FormItem'
import FormList from './form/FormList'

const LAYOUT_CLASSES = {
  horizontal: 'ui-form-horizontal',
  vertical: 'ui-form-vertical',
  inline: 'ui-form-inline flex flex-wrap items-start gap-3',
}

/**
 * antd Form. Owns the store (its own when `form` is not supplied), wires the
 * callbacks, and submits through `validateFields` so `onFinish` only sees
 * valid values, exactly like antd.
 */
const Form = ({
  form: externalForm,
  layout = 'horizontal',
  initialValues,
  onFinish,
  onFinishFailed,
  onValuesChange,
  onFieldsChange,
  disabled = false,
  preserve = true,
  name,
  labelCol,
  wrapperCol,
  labelAlign,
  colon,
  size,
  component = 'form',
  scrollToFirstError = false,
  requiredMark,
  validateTrigger,
  className,
  style,
  children,
  ...rest
}) => {
  const [form] = useForm(externalForm)
  const initialised = useRef(false)

  // Applied during the first render, not in an effect: child Form.Item effects
  // run before the parent's, so an effect here would land after their
  // registration. Later `initialValues` changes are ignored, as in antd.
  if (!initialised.current) {
    form.setInitialValues(initialValues)
    initialised.current = true
  }

  useEffect(() => {
    form.setCallbacks({ onFinish, onFinishFailed, onValuesChange, onFieldsChange })
  }, [form, onFieldsChange, onFinish, onFinishFailed, onValuesChange])

  const context = useMemo(
    () => ({ form, layout, disabled, preserve, labelCol, wrapperCol, colon, size, validateTrigger }),
    [colon, disabled, form, labelCol, layout, preserve, size, validateTrigger, wrapperCol]
  )

  const handleSubmit = (event) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    form.submit()
  }

  const Component = component === false ? 'div' : component

  return (
    <FormContext.Provider value={context}>
      <FieldPrefixContext.Provider value={[]}>
        <Component
          name={name}
          noValidate
          onSubmit={handleSubmit}
          onReset={(event) => {
            event.preventDefault()
            form.resetFields()
          }}
          className={cn('ui-form', LAYOUT_CLASSES[layout], className)}
          style={style}
          {...rest}
        >
          {typeof children === 'function' ? children(form) : children}
        </Component>
      </FieldPrefixContext.Provider>
    </FormContext.Provider>
  )
}

const Provider = ({ children }) => children

Form.Item = FormItem
Form.List = FormList
Form.useForm = useForm
Form.useFormInstance = useFormInstance
Form.useWatch = useWatch
Form.Provider = Provider
Form.ErrorList = ({ errors = [] }) =>
  errors.length === 0 ? null : (
    <div role="alert" className="ui-form-item-explain-error text-xs text-[var(--error-color)]">
      {errors.map((message) => (
        <div key={String(message)}>{message}</div>
      ))}
    </div>
  )

export { useForm, useFormInstance, useWatch }
export default Form
